/**
 * Indian GST (goods & services tax) — notified slabs, place of supply, CGST/SGST vs IGST.
 * References: CGST Act / IGST Act — intra-state supplies attract CGST+SGST (equal halves of
 * the headline rate); inter-state supplies attract IGST at the full rate.
 */

/** Notified GST % on services commonly used in India (nil, 5, 12, 18, 28). */
export const INDIAN_SERVICE_GST_SLABS = [0, 5, 12, 18, 28] as const;

/** Default when API omits rate — 18% is the general services slab. */
export const DEFAULT_GST_PERCENT = 18;

/** @deprecated Use DEFAULT_GST_PERCENT; kept for imports that expect a decimal rate. */
export const GST_RATE = DEFAULT_GST_PERCENT / 100;

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Format an amount as Indian Rupees with no decimals, e.g. ₹14,999. */
export function formatInr(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
}

/** Snap to nearest notified service slab (invalid API values). */
export function snapToIndianServiceGstSlab(percent: number): number {
  const p = Math.max(0, Math.min(100, percent));
  const slabs = INDIAN_SERVICE_GST_SLABS as readonly number[];
  if (slabs.includes(p)) return p;
  return slabs.reduce((best, s) => (Math.abs(s - p) < Math.abs(best - p) ? s : best));
}

/** Service fee per order — use 0 or from API. */
export const getServiceFee = (subtotal: number, config?: { serviceFeePercent?: number; serviceFeeFixed?: number }): number => {
  if (!config) return 0;
  const pct = (config.serviceFeePercent ?? 0) / 100;
  const fixed = config.serviceFeeFixed ?? 0;
  return roundMoney(subtotal * pct + fixed);
};

/** Delivery / billing address state vs platform place of supply (intra = CGST+SGST, inter = IGST). */
export interface GstSupplyContext {
  customerState?: string;
  /** Overrides REACT_APP_GST_SUPPLY_STATE when set. */
  supplierState?: string;
}

export interface GstInvoiceRow {
  key: string;
  label: string;
  amount: number;
}

export interface PricingBreakdown {
  subtotal: number;
  serviceFee: number;
  taxableValue: number;
  /** Effective GST ÷ taxable base (for reference). */
  gstRate: number;
  gstAmount: number;
  /** Short label, e.g. "18%" or "Mixed rates". */
  gstLabel: string;
  /** Rows for invoice UI — CGST+SGST, IGST, or combined GST. */
  gstInvoiceRows: GstInvoiceRow[];
  discount: number;
  finalTotal: number;
  originalTotal: number;
}

/** Cart line — gstPercent is whole number matching a notified slab after normalization. */
export interface CartLineForPricing {
  price: number;
  quantity: number;
  gstPercent?: number;
}

export function cartItemsToPricingLines(
  items: { price: number; quantity?: number; gstPercent?: number; gst_percent?: number }[]
): CartLineForPricing[] {
  return (items || []).map((i) => ({
    price: i.price,
    quantity: i.quantity ?? 1,
    gstPercent: i.gstPercent ?? i.gst_percent,
  }));
}

/**
 * Normalize API values (18 or 0.18) and snap to Indian service GST slabs.
 */
export function normalizeGstPercent(raw?: number | null): number {
  if (raw == null || Number.isNaN(Number(raw))) return DEFAULT_GST_PERCENT;
  const n = Number(raw);
  if (n < 0) return DEFAULT_GST_PERCENT;
  let percent: number;
  if (n > 0 && n <= 1) {
    percent = Math.round(n * 10000) / 100;
  } else {
    percent = Math.min(100, n);
  }
  return snapToIndianServiceGstSlab(percent);
}

export function gstPercentFromUnknown(item: Record<string, unknown> | null | undefined): number | undefined {
  if (!item) return undefined;
  const v = item.gst_percent ?? item.gstPercent ?? item.tax_percent ?? item.service_gst;
  if (v == null || v === '') return undefined;
  const n = Number(v);
  if (Number.isNaN(n)) return undefined;
  return normalizeGstPercent(n);
}

function normalizeStateKey(s: string | undefined): string {
  if (!s || typeof s !== 'string') return '';
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

function getEffectiveSupplierState(ctx?: GstSupplyContext): string {
  const fromCtx = ctx?.supplierState?.trim();
  if (fromCtx) return fromCtx;
  const env = typeof process !== 'undefined' && process.env?.REACT_APP_GST_SUPPLY_STATE;
  return (env && String(env).trim()) || '';
}

/** Intra-state supply: customer state and supplier state match (same UT/state for supply). */
export function isIndianIntraStateSupply(customerState: string | undefined, supplierState: string | undefined): boolean {
  const c = normalizeStateKey(customerState);
  const p = normalizeStateKey(supplierState);
  return Boolean(c && p && c === p);
}

function getGstLabelForLines(lineRates: number[]): string {
  if (lineRates.length === 0) return `${DEFAULT_GST_PERCENT}%`;
  const uniq = Array.from(new Set(lineRates)).sort((a, b) => a - b);
  if (uniq.length === 1) return `${uniq[0]}%`;
  return 'Mixed rates';
}

function buildGstInvoiceRows(params: {
  gstAmount: number;
  gstLabel: string;
  lineGstAmounts: number[];
  lineRates: number[];
  ctx?: GstSupplyContext;
}): GstInvoiceRow[] {
  const { gstAmount, gstLabel, lineGstAmounts, lineRates, ctx } = params;
  const supplier = getEffectiveSupplierState(ctx);
  const customer = ctx?.customerState?.trim() || '';

  if (!supplier || !customer) {
    return [{ key: 'gst', label: `GST (${gstLabel})`, amount: gstAmount }];
  }

  if (!isIndianIntraStateSupply(customer, supplier)) {
    const uniform = new Set(lineRates).size <= 1;
    const r = lineRates[0] ?? DEFAULT_GST_PERCENT;
    const label = uniform ? `IGST (${r}%)` : 'IGST (mixed rates)';
    return [{ key: 'igst', label, amount: gstAmount }];
  }

  let cgst = 0;
  let sgst = 0;
  for (let i = 0; i < lineGstAmounts.length; i++) {
    const g = lineGstAmounts[i];
    const half = roundMoney(g / 2);
    cgst += half;
    sgst += roundMoney(g - half);
  }
  cgst = roundMoney(cgst);
  sgst = roundMoney(sgst);
  const drift = roundMoney(gstAmount - cgst - sgst);
  sgst = roundMoney(sgst + drift);

  const uniform = new Set(lineRates).size === 1;
  if (uniform) {
    const r = lineRates[0] ?? DEFAULT_GST_PERCENT;
    const halfRate = r / 2;
    return [
      { key: 'cgst', label: `CGST (${halfRate}%)`, amount: cgst },
      { key: 'sgst', label: `SGST (${halfRate}%)`, amount: sgst },
    ];
  }
  return [
    { key: 'cgst', label: 'CGST', amount: cgst },
    { key: 'sgst', label: 'SGST', amount: sgst },
  ];
}

/**
 * Per-line taxable value (line + pro-rata service fee) × notified slab; then Indian GST display split.
 */
export function computePricingFromCart(
  lines: CartLineForPricing[],
  discount: number = 0,
  serviceFeeConfig?: { serviceFeePercent?: number; serviceFeeFixed?: number },
  gstSupplyContext?: GstSupplyContext
): PricingBreakdown {
  const subtotal = lines.reduce((s, l) => s + l.price * (l.quantity || 1), 0);
  const serviceFee = getServiceFee(subtotal, serviceFeeConfig);
  const taxableBase = roundMoney(subtotal + serviceFee);
  const appliedDiscount = Math.min(Math.max(0, roundMoney(discount)), taxableBase);
  const discountRatio = taxableBase > 0 ? appliedDiscount / taxableBase : 0;

  const lineGstAmounts: number[] = [];
  const originalLineGstAmounts: number[] = [];
  const lineRates: number[] = [];

  if (subtotal > 0 && lines.length > 0) {
    for (const line of lines) {
      const lineSub = line.price * (line.quantity || 1);
      const feeShare = serviceFee * (lineSub / subtotal);
      const taxable = lineSub + feeShare;
      const discountedTaxable = roundMoney(taxable * (1 - discountRatio));
      const ratePct = normalizeGstPercent(line.gstPercent);
      lineRates.push(ratePct);
      const lineGst = roundMoney(discountedTaxable * (ratePct / 100));
      lineGstAmounts.push(lineGst);
      originalLineGstAmounts.push(roundMoney(taxable * (ratePct / 100)));
    }
  }

  const gstAmount = roundMoney(lineGstAmounts.reduce((a, b) => a + b, 0));
  const originalGstAmount = roundMoney(originalLineGstAmounts.reduce((a, b) => a + b, 0));
  const originalTotal = roundMoney(taxableBase + originalGstAmount);
  const taxableValue = roundMoney(taxableBase - appliedDiscount);
  const finalTotal = roundMoney(taxableValue + gstAmount);
  const gstRate = taxableValue > 0 ? gstAmount / taxableValue : DEFAULT_GST_PERCENT / 100;
  const gstLabel = getGstLabelForLines(lineRates);

  const gstInvoiceRows = buildGstInvoiceRows({
    gstAmount,
    gstLabel,
    lineGstAmounts,
    lineRates,
    ctx: gstSupplyContext,
  });

  return {
    subtotal,
    serviceFee,
    taxableValue,
    gstRate,
    gstAmount,
    gstLabel,
    gstInvoiceRows,
    discount: appliedDiscount,
    finalTotal,
    originalTotal,
  };
}

export function computePricing(
  subtotal: number,
  discount: number = 0,
  serviceFeeConfig?: { serviceFeePercent?: number; serviceFeeFixed?: number },
  gstSupplyContext?: GstSupplyContext
): PricingBreakdown {
  return computePricingFromCart(
    [{ price: subtotal, quantity: 1, gstPercent: DEFAULT_GST_PERCENT }],
    discount,
    serviceFeeConfig,
    gstSupplyContext
  );
}
